<!--
Componente compartilhado para renderização da lista de sugestões.
Encapsula a lógica duplicada entre SearchSuggestField.vue e SearchSuggestDialogPanel.vue
-->
<script setup lang="ts">
import type { Suggestion, SuggestionGroupKey } from './types';
import type { SkeletonVariants } from '~/components/ui/skeleton';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { ClassValue } from 'clsx';

import { CommandSeparator } from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';

import HistoryGroupIcon from '~icons/material-symbols-light/history';

import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';

interface SearchSuggestListContentProps {
	loading: boolean;
	shouldShowEmptyState: boolean;
	shouldShowLastResearchs: boolean;
	shouldShowCodeGroup: boolean;
	hasMinSearchTerm: boolean;
	searchTerm: string;
	isFetchingPropertyByCodeSearch?: boolean;
	skeletonVariant?: Extract<SkeletonVariants['variant'], 'primary' | 'white'>;
	filteredLastResearchs: Suggestion[];
	locationAutocompleteData?: SuggestionGroup[];
	searchByCodeOption: Suggestion[];

	// Class bindings
	emptyStateWrapperClasses?: ClassValue;
	emptyStateIconWrapperClasses?: ClassValue;
	emptyStateIconClasses?: ClassValue;
	emptyStateTitleClasses?: ClassValue;
	emptyStateDescriptionClasses?: ClassValue;
	separatorClasses?: ClassValue;
	groupWrapperClasses?: ClassValue;
	groupHeadingClasses?: ClassValue;
	groupIconClasses?: ClassValue;
	groupItemWrapperClasses?: ClassValue;
	groupItemTextClasses?: ClassValue;
	groupItemSubTextClasses?: ClassValue;
	variantStyles?: {
		separator: string;
		emptyStateIconWrapper?: string;
		emptyStateIconSubWrapper?: string;
		emptyStateIcon: string;
		emptyStateTitle?: string;
		emptyStateDescription?: string;
	};
}

const props = withDefaults(defineProps<SearchSuggestListContentProps>(), {
	isFetchingPropertyByCodeSearch: false,
	skeletonVariant: 'white'
});

const emit = defineEmits<{
	'remove-suggestion': [key: Suggestion];
}>();
</script>

<template>
	<div
		v-show="shouldShowEmptyState"
		:class="cn('px-5 py-6', emptyStateWrapperClasses)"
	>
		<ListingEmptyState
			:show-badges="false"
			:icon-wrapper-classes="
				cn(
					variantStyles?.emptyStateIconWrapper ?? 'size-[47.5px]',
					emptyStateIconWrapperClasses
				)
			"
			:icon-sub-wrapper-classes="variantStyles?.emptyStateIconSubWrapper"
			:icon-classes="cn('size-[22.5px]', variantStyles?.emptyStateIcon)"
			:title-classes="
				cn('text-lg', variantStyles?.emptyStateTitle, emptyStateTitleClasses)
			"
			:description-classes="
				cn(
					'text-sm',
					variantStyles?.emptyStateDescription,
					emptyStateDescriptionClasses
				)
			"
		>
			<template #title>Nenhum resultado encontrado</template>
			<template #description>
				Tente uma busca diferente ou verifique a grafia
			</template>
		</ListingEmptyState>
	</div>

	<CommandSeparator
		v-if="shouldShowLastResearchs && hasMinSearchTerm"
		:class="cn(variantStyles?.separator, separatorClasses)"
	/>

	<NewGroup
		v-if="shouldShowLastResearchs"
		:suggestion-key="'lastResearchs'"
		:heading="{
			text: 'Últimas buscas',
			icon: HistoryGroupIcon
		}"
		:suggestions="filteredLastResearchs"
		:search-term="searchTerm"
		is-removable
		:class="cn(groupWrapperClasses)"
		:group-heading-classes="cn(groupHeadingClasses)"
		:group-icon-classes="cn(groupIconClasses)"
		:group-item-wrapper-classes="cn(groupItemWrapperClasses)"
		:group-item-text-classes="cn(groupItemTextClasses)"
		:group-item-sub-text-classes="cn(groupItemSubTextClasses)"
		@remove-suggestion="emit('remove-suggestion', $event)"
	/>

	<template v-if="loading">
		<LoadingGroup
			v-for="index in 3"
			:key="`loading-group-${index}`"
			:active="true"
			:group-heading-classes="groupHeadingClasses"
			:group-icon-classes="groupIconClasses"
			:group-item-wrapper-classes="groupItemWrapperClasses"
			:group-item-text-classes="groupItemTextClasses"
			:group-item-sub-text-classes="groupItemSubTextClasses"
			:skeleton-variant="skeletonVariant"
		/>
	</template>

	<template v-if="!loading && locationAutocompleteData?.length">
		<template
			v-for="(suggestionGroup, index) in locationAutocompleteData"
			:key="`suggestion-group-${suggestionGroup.key}`"
		>
			<CommandSeparator
				:class="[
					cn(variantStyles?.separator, separatorClasses),
					{ hidden: !suggestionGroup.visible || index === 0 }
				]"
			/>

			<NewGroup
				:suggestion-key="suggestionGroup.key as SuggestionGroupKey"
				:heading="suggestionGroup.heading"
				:suggestions="suggestionGroup.suggestions"
				:search-term="searchTerm"
				:class="cn(groupWrapperClasses)"
				:group-heading-classes="cn(groupHeadingClasses)"
				:group-icon-classes="cn(groupIconClasses)"
				:group-item-wrapper-classes="cn(groupItemWrapperClasses)"
				:group-item-text-classes="cn(groupItemTextClasses)"
				:group-item-sub-text-classes="cn(groupItemSubTextClasses)"
			/>
		</template>
	</template>

	<CodeGroup
		v-if="shouldShowCodeGroup"
		:search-term="searchTerm"
		suggestion-key="code"
		:suggestions="searchByCodeOption"
		:is-fetching-property-by-code-search="isFetchingPropertyByCodeSearch"
		:class="cn(groupWrapperClasses)"
		:group-heading-classes="cn(groupHeadingClasses)"
		:group-icon-classes="cn(groupIconClasses)"
		:group-item-wrapper-classes="cn(groupItemWrapperClasses)"
		:group-item-text-classes="cn(groupItemTextClasses)"
		:group-item-sub-text-classes="cn(groupItemSubTextClasses)"
	/>
</template>
